/**
 * Volto stores every draggable block collection as a `blocks` mapping plus
 * `blocks_layout.items`. The page is one collection; container blocks can
 * own more collections below it (for example a columns block owns column
 * wrappers, and each wrapper owns its content blocks).
 */

const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isBlockCollection = (value) =>
  isObject(value?.blocks) && Array.isArray(value?.blocks_layout?.items);

const nestedCollections = (block) => {
  const collections = [];
  if (isBlockCollection(block)) collections.push(block);
  if (isBlockCollection(block?.data)) collections.push(block.data);
  return collections;
};

/**
 * Return every ordered collection, keyed by the ID of its owning block.
 * IDs generated by Volto are page-unique; the root page uses `root`.
 */
export const collectBlockOrders = (page) => {
  const orders = new Map();
  const visited = new Set();

  const visitCollection = (collection, ownerId) => {
    if (!isBlockCollection(collection) || visited.has(collection)) return;
    visited.add(collection);

    const items = collection.blocks_layout.items;
    orders.set(ownerId, [...items]);
    items.forEach((blockId) => {
      const block = collection.blocks[blockId];
      nestedCollections(block).forEach((nested, index) =>
        visitCollection(nested, `${blockId}:${index}`),
      );
    });
  };

  visitCollection(page, 'root');
  return orders;
};

export const collectBlockIds = (page) => {
  const ids = new Set();
  collectBlockOrders(page).forEach((items) =>
    items.forEach((blockId) => ids.add(blockId)),
  );
  return ids;
};

/**
 * Find a block anywhere in the page's nested block collections. `rootId`
 * identifies the top-level block containing the match, which lets callers
 * reject a stale nested selection after the user selects another container.
 */
export const findBlock = (page, targetId) => {
  if (!targetId) return null;

  const visited = new Set();
  const visitCollection = (collection, rootId = null) => {
    if (!isBlockCollection(collection) || visited.has(collection)) return null;
    visited.add(collection);

    for (const blockId of collection.blocks_layout.items) {
      const block = collection.blocks[blockId];
      const blockRootId = rootId || blockId;
      if (blockId === targetId) {
        return { id: blockId, block, rootId: blockRootId };
      }
      for (const nested of nestedCollections(block)) {
        const match = visitCollection(nested, blockRootId);
        if (match) return match;
      }
    }
    return null;
  };

  return visitCollection(page);
};

/**
 * IDs whose ordered collection changed between two states. Returning the
 * full membership of changed collections lets FLIP animate both the item
 * deliberately moved and every neighbour displaced by it.
 */
export const changedOrderIds = (beforePage, afterPage) => {
  const beforeOrders = collectBlockOrders(beforePage);
  const afterOrders = collectBlockOrders(afterPage);
  const changedIds = new Set();
  const owners = new Set([...beforeOrders.keys(), ...afterOrders.keys()]);

  owners.forEach((ownerId) => {
    const before = beforeOrders.get(ownerId) || [];
    const after = afterOrders.get(ownerId) || [];
    const changed =
      before.length !== after.length ||
      before.some((blockId, index) => blockId !== after[index]);
    if (changed)
      [...before, ...after].forEach((blockId) => changedIds.add(blockId));
  });

  return changedIds;
};

/**
 * Column wrappers are real draggable items in the state, but the columns
 * add-on does not put their IDs on its Grid.Column DOM nodes. This supplies
 * the ordered column IDs needed to index those nodes for the animation.
 */
export const collectColumnBlocks = (page) => {
  const columns = [];
  const visited = new Set();

  const visitCollection = (collection) => {
    if (!isBlockCollection(collection) || visited.has(collection)) return;
    visited.add(collection);

    collection.blocks_layout.items.forEach((blockId) => {
      const block = collection.blocks[blockId];
      if (
        block?.['@type'] === 'columnsBlock' &&
        isBlockCollection(block.data)
      ) {
        columns.push({
          blockId,
          columnIds: [...block.data.blocks_layout.items],
        });
      }
      nestedCollections(block).forEach(visitCollection);
    });
  };

  visitCollection(page);
  return columns;
};
